import api from '@app/api'

import { CONTENT_TYPES } from '@app/api/constants'

import {
  GetQuotasParams,
  GetListsParams,
  DeleteListsData,
  CreateListsData,
  GetListsItemParams,
  GetListsItemProducts,
  ApproveListsData,
  PatchListsItemProductData,
  ChangeListsItemProductsData,
  CopyListData,
  ImportListsItemsProductsFromFileParams,
  GetMonitoringCreatingTasksParams,
  GetArchivesListsParams,
  ExportExcel,
  CopyArchivesItem,
  CreatingTasksData,
  GetMonitoringCreatingTotalTasksParams,
  GetMonitoringListsSummaryParams,
  GetMonitoringListsSummaryByIdParams,
  ExportMonitoringList,
  PostListsItemProductsData,
  ExportMonitoringArchivesList
} from './types'

import type { DefaultParams } from '@app/api/types'
import type { MultiStatusResponseDto, AblePaginatedResponseFn, ResponseDto } from '@app/api/dtos'

import type {
  QuotasDto,
  ProductDto,
  CreatingTasksDto,
  RolesDirectorDto,
  MonitoringListDto,
  MonitoringListSummaryDto,
  MonitoringListArchivesDto,
  MonitoringListCreatingTasksDto,
  MonitoringListTotalCreatingTasksDto
} from './dtos'

import type { RetrieveReport } from '@app/entities/report'

const URL = '/monitoring-lists/'

export const getLists: AblePaginatedResponseFn<GetListsParams, MonitoringListDto> = async (params?: GetListsParams) => {
  const response = await api.get(URL, { params })
  return response.data
}

export const getArchivesLists = async (
  params?: GetArchivesListsParams
): Promise<ResponseDto<MonitoringListArchivesDto>> => {
  const response = await api.get<ResponseDto<MonitoringListArchivesDto>>(`${URL}archives/`, { params })
  return response.data
}

export const createLists = async (data: CreateListsData): Promise<void> => {
  await api.post(URL, data)
}

export const approveLists = async (data: ApproveListsData): Promise<MultiStatusResponseDto> => {
  const response = await api.post<MultiStatusResponseDto>(`${URL}approve/`, data)
  return response.data
}

export const deleteLists = async (data: DeleteListsData): Promise<void> => {
  await api.delete(URL, { data })
}

export const getListsCurrentItem = async (params: GetListsItemParams): Promise<MonitoringListDto> => {
  const { id, ...queryParams } = params

  const response = await api.get<MonitoringListDto>(`${URL}${id}/`, { params: queryParams })
  return response.data
}

export const getListsArchivalItem = async (id: number): Promise<MonitoringListDto> => {
  const response = await api.get<MonitoringListDto>(`${URL}archives/${id}/list-data/`)
  return response.data
}

export const getListsItemProducts = async (params: GetListsItemProducts): Promise<ResponseDto<ProductDto>> => {
  const { id, ...queryParams } = params

  const response = await api.get<ResponseDto<ProductDto>>(`${URL}${id}/products`, { params: queryParams })
  return response.data
}

export const getListsArchivalItemProducts = async (params: GetListsItemProducts): Promise<ResponseDto<ProductDto>> => {
  const { id, ...queryParams } = params

  const response = await api.get<ResponseDto<ProductDto>>(`${URL}archives/${id}/list-products/`, {
    params: queryParams
  })
  return response.data
}

export const postListsItemProducts = async (params: PostListsItemProductsData): Promise<void> => {
  const { id, data } = params

  await api.post<ResponseDto<ProductDto>>(`${URL}${id}/products/`, data)
}

export const getMonitoringCreatingTasks = async (
  params?: GetMonitoringCreatingTasksParams
): Promise<ResponseDto<MonitoringListCreatingTasksDto>> => {
  const response = await api.get<ResponseDto<MonitoringListCreatingTasksDto>>(`${URL}creating-tasks/`, { params })
  return response.data
}

export const getMonitoringTotalCreatingTasks = async (
  params?: GetMonitoringCreatingTotalTasksParams
): Promise<ResponseDto<MonitoringListTotalCreatingTasksDto>> => {
  const response = await api.get<ResponseDto<MonitoringListTotalCreatingTasksDto>>(`${URL}total/creating-tasks/`, {
    params
  })
  return response.data
}

export const patchListsItemProduct = async ({ id, productId, ...body }: PatchListsItemProductData): Promise<void> => {
  await api.patch<ResponseDto<ProductDto>>(`${URL}${id}/products/${productId}`, body)
}

export const deleteListsItemsProducts = async ({ id, data }: ChangeListsItemProductsData): Promise<void> => {
  await api.post(`${URL}${id}/products/remove/`, data)
}

export const restoreListsItemsProducts = async ({ id, data }: ChangeListsItemProductsData): Promise<void> => {
  await api.post(`${URL}${id}/products/restore/`, data)
}

export const copyListsItemProducts = async (data: CopyListData): Promise<void> => {
  await api.post(`${URL}copy/`, data)
}

export const getListsItemsProductsExportTemplate = async (
  { id, ...params }: ExportExcel,
  signal?: AbortSignal
): Promise<Blob> => {
  const response = await api.get<Blob>(`${URL}${id}/products/export/template/`, {
    signal,
    params,
    responseType: 'blob',
    headers: {
      'Content-Type': CONTENT_TYPES.applicationOfficedocumentSpreadsheetml
    }
  })

  return response.data
}

export const importListsItemsProductsFromFile = async (
  params: ImportListsItemsProductsFromFileParams,
  data: FormData
): Promise<void> => {
  const { id } = params

  await api.post(`${URL}${id}/products/import/`, data, {
    headers: {
      'Content-Type': CONTENT_TYPES.multipartFormData
    }
  })
}

export const exportMonitoringList = async ({ id, ...params }: ExportExcel, signal?: AbortSignal): Promise<Blob> => {
  const response = await api.get<Blob>(`${URL}${id}/export/`, {
    signal,
    params,
    responseType: 'blob',
    headers: {
      'Content-Type': CONTENT_TYPES.applicationOfficedocumentSpreadsheetml
    }
  })

  return response.data
}

export const exportMonitoringLists = async (
  data: ExportMonitoringList,
  signal?: AbortSignal
): Promise<RetrieveReport> => {
  const response = await api.post<RetrieveReport>(`${URL}export/`, data, { signal })

  return response.data
}

export const exportMonitoringArchivesLists = async (
  data: ExportMonitoringArchivesList,
  signal?: AbortSignal
): Promise<Blob> => {
  const response = await api.post<Blob>(`${URL}archives/export/`, data, {
    signal,
    responseType: 'blob'
  })

  return response.data
}

export const copyArchivesItem = async (data: CopyArchivesItem): Promise<void> => {
  // TODO: mock url, проверить/исправить как бэк будет готов
  await api.post(`${URL}archives/copy/`, data)
}

export const exportCompareArchives = async ({ id, ...params }: ExportExcel, signal?: AbortSignal): Promise<Blob> => {
  const response = await api.get<Blob>(`${URL}archives/${id}/compare/`, {
    signal,
    params,
    responseType: 'blob',
    headers: {
      'Content-Type': CONTENT_TYPES.applicationOfficedocumentSpreadsheetml
    }
  })
  return response.data
}

export const exportArchivesItem = async ({ id, ...params }: ExportExcel, signal?: AbortSignal): Promise<Blob> => {
  const response = await api.get<Blob>(`${URL}archives/${id}/export/`, {
    signal,
    params,
    responseType: 'blob',
    headers: {
      'Content-Type': CONTENT_TYPES.applicationOfficedocumentSpreadsheetml
    }
  })
  return response.data
}

export const patchCreatingTasks = async (data: CreatingTasksData): Promise<ResponseDto<CreatingTasksDto>> => {
  const response = await api.patch<ResponseDto<CreatingTasksDto>>(`${URL}creating-tasks/`, data)
  return response.data
}

export const getMonitoringListsSummary = async (
  params?: GetMonitoringListsSummaryParams
): Promise<MonitoringListSummaryDto> => {
  const response = await api.get<MonitoringListSummaryDto>(`${URL}summary/`, { params })
  return response.data
}

export const getMonitoringListsSummaryById = async ({
  id,
  ...params
}: GetMonitoringListsSummaryByIdParams): Promise<MonitoringListSummaryDto> => {
  const response = await api.get<MonitoringListSummaryDto>(`${URL}${id}/summary/`, { params })
  return response.data
}

export const getRolesDirectors: AblePaginatedResponseFn<DefaultParams, RolesDirectorDto> = async params => {
  const response = await api.get(`${URL}roles/directors/`, { params })
  return response.data
}

export const getQuotas = async ({ id, ...params }: GetQuotasParams): Promise<QuotasDto> => {
  const response = await api.get<QuotasDto>(`${URL}${id}/quotas/`, { params })
  return response.data
}
